<template>
  <section class="popular-courses">
    <div class="section-header">
      <span class="section-label">{{ t('courseCategory.popularCourses.label') }}</span>
      <h2 class="section-title">{{ t('courseCategory.popularCourses.title') }}</h2>
    </div>

    <!-- Скелетон поки грузятся дані з бэка -->
    <div v-if="loading" class="popular-courses-skeleton" aria-hidden="true">
      <div class="popular-courses-skeleton__grid">
        <CourseCardSkeleton v-for="i in 4" :key="i" />
      </div>
    </div>

    <template v-else>
      <div class="courses-wrapper">
        <div class="courses-slider" ref="sliderRef">
          <div class="courses-track" :style="trackStyle">
            <PopularCourseCard
              v-for="course in popularCourses"
              :key="course.id"
              :course="course"
              class="course-slide"
            />
          </div>
        </div>

        <div class="slider-controls">
          <!-- <div class="pagination">
            <button 
              v-for="(_, index) in totalPages" 
              :key="index" 
              class="dot" 
              :class="{ active: index === currentIndex }"
              @click="goToSlide(index)"
            ></button>
          </div> -->
          <div class="nav-arrows">
            <button
              type="button"
              class="nav-btn prev"
              :aria-label="t('index.carousel.prev')"
              @click="prevSlide"
              :disabled="isFirstSlide"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
                <path d="M15 18L9 12L15 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </button>
            <button
              type="button"
              class="nav-btn next"
              :aria-label="t('index.carousel.next')"
              @click="nextSlide"
              :disabled="isLastSlide"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
                <path d="M9 18L15 12L9 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </button>
          </div>
        </div>
      </div>
    </template>
  </section>
</template>

<script setup lang="ts">
import { useCourseStore } from '@/stores/course'
import { useSlider } from '@/composables/ui'

interface Props {
  locale: string
}

const props = defineProps<Props>()
const { t } = useI18n()
const courseStore = useCourseStore()

const loadPopularCourses = async () => {
  try {
    await courseStore.fetchPopularCourses({
      locale: props.locale,
      limit: 10,
    })
  } catch (error) {
    console.error('Failed to load popular courses:', error)
  }
}

// Загружаем и обновляем популярные курсы при изменении locale-пропа
watch(
  () => props.locale,
  async () => {
    await loadPopularCourses()
  },
  { immediate: true },
)

// Используем популярные курсы из store
const popularCourses = computed(() => courseStore.popularCourses)

const loading = computed(() => courseStore.loading && courseStore.popularCourses.length === 0)

const coursesCount = computed(() => popularCourses.value.length)

// Реактивный itemsPerView — совпадает с CSS-брейкпоинтами (.course-slide flex widths)
const windowWidth = ref(typeof window !== 'undefined' ? window.innerWidth : 1200)
onMounted(() => {
  const onResize = () => { windowWidth.value = window.innerWidth }
  window.addEventListener('resize', onResize)
  onUnmounted(() => window.removeEventListener('resize', onResize))
})
const itemsPerView = computed(() => {
  if (windowWidth.value <= 575) return 1   // flex: 0 0 100%
  if (windowWidth.value <= 991) return 2   // flex: 0 0 calc(50% - 12px)
  if (windowWidth.value <= 1200) return 3  // flex: 0 0 calc(33.333% - 16px)
  return 4                                 // flex: 0 0 calc(25% - 18px)
})

const {
  currentIndex,
  sliderRef,
  totalPages,
  trackStyle,
  isFirstSlide,
  isLastSlide,
  prevSlide,
  nextSlide,
  goToSlide
} = useSlider({
  itemsCount: coursesCount,
  itemsPerView,
})
</script>

<style scoped>
.popular-courses {
  padding: 100px 0;
  max-width: var(--max-width-container);
  margin: 0 auto;
}

/* Контейнер заголовка секції (базові .section-label, .section-title з index.css) */
.popular-courses .section-header {
  max-width: var(--max-width-container);
  margin: 0 auto;
  padding: 0 40px;
  text-align: center;
  margin-bottom: 68px;
}

.courses-wrapper {
  max-width: var(--max-width-container);
  margin: 0 auto;
  padding: 0 40px;
}

.courses-slider {
  overflow: hidden;
}

.popular-courses-skeleton {
  max-width: var(--max-width-container);
  margin: 0 auto;
  padding: 0 40px;
}

.popular-courses-skeleton__grid {
  display: flex;
  gap: 24px;
  flex-wrap: wrap;
  justify-content: center;
}

.popular-courses-skeleton__grid > * {
  flex: 0 0 calc(25% - 18px);
  min-width: 0;
}

@media (max-width: 1200px) {
  .popular-courses-skeleton__grid > * {
    flex: 0 0 calc(33.333% - 16px);
  }
}

@media (max-width: 991px) {
  .popular-courses-skeleton {
    padding: 0 30px;
  }

  .popular-courses-skeleton__grid > * {
    flex: 0 0 calc(50% - 12px);
  }
}

@media (max-width: 767px) {
  .popular-courses-skeleton {
    padding: 0 20px;
  }
}

@media (max-width: 575px) {
  .popular-courses-skeleton__grid > * {
    flex: 0 0 100%;
  }
}

.courses-track {
  display: flex;
  gap: 24px;
  transition: transform 0.4s ease;
}

.course-slide {
  flex: 0 0 calc(25% - 18px);
  min-width: 0;
}

.slider-controls {
  margin-top: 41px;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 32px;
}

.pagination {
  display: flex;
  gap: 8px;
}

.dot {
  width: 24px;
  height: 4px;
  border-radius: 2px;
  border: none;
  background: #E8E8E8;
  cursor: pointer;
  transition: all 0.2s;
  padding: 0;
}

.dot.active {
  background: linear-gradient(90deg, #09AFEA 0%, #19E089 100%);
  width: 32px;
}

.dot:hover:not(.active) {
  background: #ccc;
}

.nav-arrows {
  display: flex;
  gap: 12px;
}

.nav-btn {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  border: 1px solid #E8E8E8;
  background: transparent;
  color: var(--color-grey);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
}

.nav-btn:hover:not(:disabled) {
  border-color: var(--color-teal);
  color: var(--color-teal);
}

.nav-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

@media (max-width: 1400px) {
  .popular-courses .section-header,
  .courses-wrapper {
    padding: 0 60px;
  }
}

@media (max-width: 1200px) {
  .course-slide {
    flex: 0 0 calc(33.333% - 16px);
  }
}

@media (max-width: 991px) {
  .popular-courses {
    padding: 60px 0;
  }

  .popular-courses .section-header,
  .courses-wrapper {
    padding: 0 30px;
  }

  .course-slide {
    flex: 0 0 calc(50% - 12px);
  }
}

@media (max-width: 767px) {
  .popular-courses {
    padding: 40px 0;
  }

  .popular-courses .section-header,
  .courses-wrapper {
    padding: 0 20px;
  }
}

@media (max-width: 575px) {
  .course-slide {
    flex: 0 0 100%;
  }
  
  .slider-controls {
    flex-direction: column;
    gap: 20px;
  }
  
  .nav-btn {
    width: 50px;
    height: 50px;
  }
}
@media (max-width: 480px) {
  .popular-courses {
    padding: 51px 0 0 0;
  }
  .popular-courses .section-header,
  .courses-wrapper {
    margin-bottom: 0;
  }
  .nav-btn {
    width: 50px;
    height: 50px;
  }
}
</style>

